import { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import PermissionsEditor from "@/components/shared/PermissionsEditor";

import { useAuth } from "@/lib/auth";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { toast } from "sonner";
const ROLE_OPTIONS_BY_ORG_TYPE = {
  POLICE: ["org_admin", "operator", "dispatcher", "viewer"],
  AMBULANCE: ["org_admin", "operator", "dispatcher", "viewer"],
  FIRE_DEPARTMENT: ["org_admin", "operator", "dispatcher", "viewer"],
  CUSTOMER: ["owner"],
};
const DEFAULT_PAGE_SIZE = 20;
const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const INITIAL_ADD_USER_FORM = {
  email: "",
  username: "",
  password: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  state: "",
  city: "",
  district: "",
  pincode: "",
  address: "",
  blood_group: "",
  emergency_contact: "",
  role: "",
};

const UserManagement = ({ orgId, loadingOrg }) => {
  const { orgId: sessionOrgId, user, can } = useAuth();
  const canManagePermissions = can("USERS.MANAGE_PERMISSIONS");
  const canCreateUsers = can("USERS.CREATE");
  const canUpdateUsers = can("USERS.UPDATE");
  const canDeleteUsers = can("USERS.DELETE");
  const effectiveOrgId = orgId || sessionOrgId || null;
  const [orgType, setOrgType] = useState(null);
  const [orgTypeLoading, setOrgTypeLoading] = useState(!!effectiveOrgId);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState("");
  const [addForm, setAddForm] = useState(INITIAL_ADD_USER_FORM);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState("");
  const [editUserId, setEditUserId] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL_ADD_USER_FORM);
  const [editOriginalRole, setEditOriginalRole] = useState("");
  const [editSelectedPermissions, setEditSelectedPermissions] = useState([]);
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);
  const [roleChangeConfirmPending, setRoleChangeConfirmPending] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filteredUsers = users.filter(
    (u) =>
      ((u.first_name || "") + " " + (u.last_name || "")).toLowerCase().includes(search.toLowerCase()) ||
      (u.username || u.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.phone_number || u.mobile || u.phone || "").includes(search)
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const params = { page: page || 1, page_size: pageSize || DEFAULT_PAGE_SIZE };

    // Prefer explicit organization filter if provided, else use effectiveOrgId or org_type
    if (organizationFilter) {
      params.organization_id = organizationFilter;
      params.org_id = organizationFilter; // accept deprecated alias
    } else if (effectiveOrgId) {
      params.organization_id = effectiveOrgId;
      params.org_id = effectiveOrgId;
    } else {
      params.org_type = "CUSTOMER";
    }

    if (roleFilter) params.role = roleFilter;
    if (isActiveFilter !== "") params.is_active = isActiveFilter === "true";
    if (search) params.search = search;

    try {
      const { data } = await api.get("/users", { params });
      const list = Array.isArray(data?.data) ? data.data : data?.data || [];
      setUsers(list);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveOrgId, page, pageSize, organizationFilter, roleFilter, isActiveFilter, search]);

  useEffect(() => {
    if (effectiveOrgId && loadingOrg) return;
    fetchUsers();
  }, [effectiveOrgId, loadingOrg, fetchUsers]);

  // Fetch org details to determine org_type for role scoping
  useEffect(() => {
    if (!effectiveOrgId) {
      setOrgType(null);
      setOrgTypeLoading(false);
      return;
    }

    setOrgTypeLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/organizations/${effectiveOrgId}`);
        setOrgType(data?.org_type || null);
      } catch {
        setOrgType(null);
      } finally {
        setOrgTypeLoading(false);
      }
    })();
  }, [effectiveOrgId]);

  const setAddField = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const getRoleOptions = () => {
    if (!orgType) return ["org_admin", "operator", "dispatcher", "viewer"];
    return ROLE_OPTIONS_BY_ORG_TYPE[orgType] || ["org_admin", "operator"];
  };

  const handleOpenAdd = () => {
    if (orgType === "CUSTOMER") {
      toast.error("Customer accounts must be created through onboarding, not via this form.");
      return;
    }
    setAddError("");
    const defaultRole = getRoleOptions()[0] || "org_admin";
    setAddForm({ ...INITIAL_ADD_USER_FORM, role: defaultRole });
    setAddOpen(true);
  };

  const handleCreateUser = async () => {
    setAddError("");

    if (orgType === "CUSTOMER") {
      setAddError("Customer accounts must be created through onboarding, not via this form.");
      return;
    }

    if (!effectiveOrgId) {
      setAddError("Organization is required to add a user.");
      return;
    }

    const validRoles = getRoleOptions();
    if (!validRoles.includes(addForm.role)) {
      setAddError(`Invalid role for this organization type. Valid roles: ${validRoles.join(", ")}`);
      return;
    }

    if (addForm.password.trim().length < 8) {
      setAddError("Password must be at least 8 characters.");
      return;
    }

    const payload = {
      organization_id: effectiveOrgId,
      email: addForm.email.trim(),
      username: addForm.username.trim(),
      password: addForm.password.trim(),
      first_name: addForm.first_name.trim(),
      last_name: addForm.last_name.trim(),
      phone_number: addForm.phone_number.trim(),
      state: addForm.state.trim(),
      city: addForm.city.trim(),
      district: addForm.district.trim(),
      pincode: addForm.pincode.trim(),
      address: addForm.address.trim(),
      blood_group: addForm.blood_group.trim(),
      emergency_contact: addForm.emergency_contact.trim(),
      role: addForm.role.trim(),
    };

    const hasMissing = Object.entries(payload).some(([key, value]) => {
      return !value;
    });

    if (hasMissing) {
      setAddError("All fields are required.");
      return;
    }

    try {
      setAddSubmitting(true);
      await api.post("/users", payload);
      toast.success("User created successfully.");
      setAddOpen(false);
      window.location.reload();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to create user.";
      setAddError(message);
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleViewUser = async (userId) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewUser(null);
    try {
      const response = await api.get(`/users/${userId}`);
      setViewUser(unwrapSingle(response));
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load user.");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleEditUser = async (userId) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditError("");
    setEditUserId(userId);
    try {
      const response = await api.get(`/users/${userId}`);
      const u = unwrapSingle(response) || {};
      const userRole = u.role || u.role_name || "";
      setEditOriginalRole(userRole);
      setEditSelectedPermissions([]);
      setEditForm({
        email: u.email || "",
        username: u.username || "",
        password: "",
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        phone_number: u.phone_number || u.mobile || "",
        state: u.state || "",
        city: u.city || "",
        district: u.district || "",
        pincode: u.pincode || "",
        address: u.address || "",
        blood_group: u.blood_group || "",
        emergency_contact: u.emergency_contact || "",
        role: userRole,
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to load user.");
      setEditOpen(false);
      setEditUserId(null);
    } finally {
      setEditLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editUserId) return;
    setEditError("");

    if (editForm.password.trim() && editForm.password.trim().length < 8) {
      setEditError("Password must be at least 8 characters.");
      return;
    }

    // Check if role is changing
    const roleChanged = editForm.role.trim() !== editOriginalRole;
    if (roleChanged) {
      setRoleChangeConfirmPending(true);
      return;
    }

    // Proceed with submission if role hasn't changed
    await submitUserUpdate();
  };

  const submitUserUpdate = async () => {
    if (!canUpdateUsers) {
      toast.error("You do not have permission to edit users.");
      return;
    }
    if (!editUserId) return;
    setEditSubmitting(true);
    setEditError("");
    const roleChanged = editForm.role.trim() !== editOriginalRole;

    const payload = {
      email: editForm.email.trim(),
      username: editForm.username.trim(),
      first_name: editForm.first_name.trim(),
      last_name: editForm.last_name.trim(),
      phone_number: editForm.phone_number.trim(),
      state: editForm.state.trim(),
      city: editForm.city.trim(),
      district: editForm.district.trim(),
      pincode: editForm.pincode.trim(),
      address: editForm.address.trim(),
      blood_group: editForm.blood_group.trim(),
      emergency_contact: editForm.emergency_contact.trim(),
      role: editForm.role.trim(),
    };

    if (editForm.password.trim()) {
      payload.password = editForm.password.trim();
    }

    try {
      await api.patch(`/users/${editUserId}`, payload);
      toast.success("User updated successfully.");
      
      // If role changed, update refresh key to force PermissionsEditor remount
      if (roleChanged) {
        setPermissionsRefreshKey((prev) => prev + 1);
      }
      
      setEditOpen(false);
      setEditUserId(null);
      await fetchUsers();
    } catch (err) {
      const { message } = extractApiError(err);
      setEditError(message || "Failed to update user.");
    } finally {
      setEditSubmitting(false);
      setRoleChangeConfirmPending(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!canDeleteUsers) {
      toast.error("You do not have permission to delete users.");
      return;
    }
    if (!deleteTarget?.id) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("User deleted successfully.");
      setDeleteOpen(false);
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to delete user.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
     <div className="space-y-5 h-full">

      {/* Search & Filter */}

      <div className="flex items-center gap-3">

        <div className="relative flex-1">

          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <Input
            placeholder="Search user..."
            className="pl-10 h-11"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

        </div>

        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-11 bg-slate-900 text-white hover:bg-slate-800"
              data-testid="add-role-btn"
            >
              <Filter className=" h-4 w-4" />
              Filter
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[320px] p-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Role</Label>
                <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v === "__any" ? "" : v)}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder={orgTypeLoading ? "Loading..." : "Select role (any)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any</SelectItem>
                    {getRoleOptions().map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Status</Label>
                <Select value={isActiveFilter} onValueChange={(v) => setIsActiveFilter(v === "__any" ? "" : v)}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!effectiveOrgId && (
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Organization ID</Label>
                  <Input placeholder="organization_id (optional)" value={organizationFilter} onChange={(e) => setOrganizationFilter(e.target.value)} />
                </div>
              )}

              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => { setPage(1); setFilterOpen(false); }}>
                  Apply
                </Button>
                <Button variant="ghost" className="flex-1" onClick={() => { setRoleFilter(""); setIsActiveFilter(""); setOrganizationFilter(""); setPage(1); setFilterOpen(false); }}>
                  Clear
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="outline"
          className="h-11 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed" 
          data-testid="add-role-btn"
          onClick={handleOpenAdd}
          disabled={!canCreateUsers || orgType === "CUSTOMER" || orgTypeLoading}
          title={!canCreateUsers ? "You do not have permission to create users." : orgTypeLoading ? "Loading organization..." : orgType === "CUSTOMER" ? "Customer accounts are created via onboarding" : ""}
        >
          <Plus className=" h-4 w-4" />
          Add New User
        </Button>

      </div>

      {/* Table */}

      <div className="rounded-xl border overflow-hidden internal-height-1">

        <table className="w-full">

          <thead className="bg-slate-100">

            <tr>

              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Sr No.</th>

              <th className="px-5 py-3 text-left font-medium uppercase text-xs">User Name</th>

              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Email</th>

              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Mobile</th>

              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Role</th>

              <th className="px-5 py-3 text-left font-medium uppercase text-xs">Status</th>

              <th className="px-5 py-3 text-center font-medium uppercase text-xs">Action</th>

            </tr>

          </thead>

          <tbody>

            {filteredUsers.map((user, index) => (

              <tr
                key={user.id}
                className="border-t hover:bg-slate-50"
              >

                <td className="px-5 py-4 font-medium uppercase text-xs">{index + 1}</td>

                <td className="px-5 py-4 font-medium uppercase text-xs">{(user.first_name || "") + (user.last_name ? ` ${user.last_name}` : "") || user.username || user.org_name || "-"}</td>

                <td className="px-5 py-4 font-medium uppercase text-xs">{user.email || user.username || "-"}</td>

                <td className="px-5 py-4 font-medium uppercase text-xs">{user.phone_number || user.mobile || user.phone || "-"}</td>

                <td className="px-5 py-4 font-medium uppercase text-xs">{user.role || user.role_name || "-"}</td>

                <td className="px-5 py-4 font-medium uppercase text-xs">

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.is_active === true
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {user.is_active === true ? "Active" : "Inactive"}
                  </span>

                </td>

                <td className="text-center">

                  <DropdownMenu>

                    <DropdownMenuTrigger asChild>

                      <Button variant="ghost" size="icon" className="bg-slate-100">

                        <MoreVertical size={18} />

                      </Button>

                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">

                      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleViewUser(user.id); }}>

                        <Eye className="mr-2 h-4 w-4" />

                        View

                      </DropdownMenuItem>

                      {canUpdateUsers ? (
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); handleEditUser(user.id); }}>

                          <Pencil className="mr-2 h-4 w-4" />

                          Edit

                        </DropdownMenuItem>
                      ) : null}

                      {canDeleteUsers ? (
                        <DropdownMenuItem
                          className="text-red-600"
                          onSelect={(e) => {
                            e.preventDefault();
                            setDeleteTarget(user);
                            setDeleteOpen(true);
                          }}
                        >

                          <Trash2 className="mr-2 h-4 w-4" />

                          Delete

                        </DropdownMenuItem>
                      ) : null}

                    </DropdownMenuContent>

                  </DropdownMenu>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Register User</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">Add a new user to the system.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">First Name</Label>
                <Input placeholder="e.g. John" value={addForm.first_name} onChange={(e) => setAddField("first_name", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Last Name</Label>
                <Input placeholder="e.g. Doe" value={addForm.last_name} onChange={(e) => setAddField("last_name", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Role</Label>
              <Select value={addForm.role} onValueChange={(value) => setAddField("role", value)} disabled={orgTypeLoading}>
                <SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed">
                  <SelectValue placeholder={orgTypeLoading ? "Loading roles..." : "Select role"} />
                </SelectTrigger>
                <SelectContent>
                  {getRoleOptions().map((role) => (
                    <SelectItem key={role} value={role}>{role.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Email</Label>
                <Input type="email" placeholder="e.g. john.doe@email.com" value={addForm.email} onChange={(e) => setAddField("email", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Username</Label>
                <Input placeholder="e.g. john.doe" value={addForm.username} onChange={(e) => setAddField("username", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Password</Label>
                <Input type="password" placeholder="Enter password" value={addForm.password} onChange={(e) => setAddField("password", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Phone Number</Label>
                <Input placeholder="e.g. +91 9876543210" value={addForm.phone_number} onChange={(e) => setAddField("phone_number", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">State</Label>
                <Input placeholder="e.g. Maharashtra" value={addForm.state} onChange={(e) => setAddField("state", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">District</Label>
                <Input placeholder="e.g. Pune" value={addForm.district} onChange={(e) => setAddField("district", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">City</Label>
                <Input placeholder="e.g. Pune" value={addForm.city} onChange={(e) => setAddField("city", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Pincode</Label>
                <Input placeholder="e.g. 411001" value={addForm.pincode} onChange={(e) => setAddField("pincode", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Address</Label>
              <Input placeholder="Enter full address" value={addForm.address} onChange={(e) => setAddField("address", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Blood Group</Label>
                <Select value={addForm.blood_group || "none"} onValueChange={(value) => setAddField("blood_group", value === "none" ? "" : value)}>
                  <SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {BLOOD_GROUP_OPTIONS.map((bg) => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Emergency Contact</Label>
                <Input placeholder="e.g. +91 9123456789" value={addForm.emergency_contact} onChange={(e) => setAddField("emergency_contact", e.target.value)} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" />
              </div>
            </div>

          </div>

          {addError && (
            <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
              {addError}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSubmitting}>Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleCreateUser} disabled={addSubmitting}>
              {addSubmitting ? "Creating..." : "Create User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">View User</DialogTitle>
          </DialogHeader>

          {viewLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="h-auto gap-2 rounded-2xl bg-slate-100 p-2">
                <TabsTrigger value="details" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Details</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><span className="font-semibold">First Name:</span> {viewUser?.first_name || "-"}</p>
                  <p><span className="font-semibold">Last Name:</span> {viewUser?.last_name || "-"}</p>
                  <p><span className="font-semibold">Email:</span> {viewUser?.email || "-"}</p>
                  <p><span className="font-semibold">Username:</span> {viewUser?.username || "-"}</p>
                  <p><span className="font-semibold">Phone Number:</span> {viewUser?.phone_number || "-"}</p>
                  <p><span className="font-semibold">Role:</span> {viewUser?.role || viewUser?.role_name || "-"}</p>
                  <p><span className="font-semibold">State:</span> {viewUser?.state || "-"}</p>
                  <p><span className="font-semibold">District:</span> {viewUser?.district || "-"}</p>
                  <p><span className="font-semibold">City:</span> {viewUser?.city || "-"}</p>
                  <p><span className="font-semibold">Pincode:</span> {viewUser?.pincode || "-"}</p>
                  <p className="col-span-2"><span className="font-semibold">Address:</span> {viewUser?.address || "-"}</p>
                  <p><span className="font-semibold">Blood Group:</span> {viewUser?.blood_group || "-"}</p>
                  <p><span className="font-semibold">Emergency Contact:</span> {viewUser?.emergency_contact || "-"}</p>
                  {Array.isArray(viewUser?.permissions) && viewUser.permissions.length > 0 && (
                    <div className="col-span-2">
                      <p className="mb-2"><span className="font-semibold">Permissions:</span></p>
                      <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-slate-50 p-2">
                        {viewUser.permissions.map((perm) => (
                          <span
                            key={perm}
                            className="inline-flex items-center rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200"
                          >
                            {perm.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl rounded-[32px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Edit User</DialogTitle>
          </DialogHeader>

          {editLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="h-auto gap-2 rounded-2xl bg-slate-100 p-2">
                <TabsTrigger value="details" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Details</TabsTrigger>
                {canManagePermissions && (
                  <TabsTrigger value="permissions" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Permissions</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="details" className="mt-4">
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">First Name</Label><Input value={editForm.first_name} onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Last Name</Label><Input value={editForm.last_name} onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
              </div>

              <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Role</Label><Select value={editForm.role} onValueChange={(value) => setEditForm((p) => ({ ...p, role: value }))}><SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"><SelectValue placeholder="Select role" /></SelectTrigger><SelectContent>{getRoleOptions().map((role) => (<SelectItem key={role} value={role}>{role.replace(/_/g, " ")}</SelectItem>))}</SelectContent></Select></div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Username</Label><Input value={editForm.username} onChange={(e) => setEditForm((p) => ({ ...p, username: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Password</Label><Input type="password" placeholder="Leave blank to keep current" value={editForm.password} onChange={(e) => setEditForm((p) => ({ ...p, password: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Phone Number</Label><Input value={editForm.phone_number} onChange={(e) => setEditForm((p) => ({ ...p, phone_number: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">State</Label><Input value={editForm.state} onChange={(e) => setEditForm((p) => ({ ...p, state: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">District</Label><Input value={editForm.district} onChange={(e) => setEditForm((p) => ({ ...p, district: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">City</Label><Input value={editForm.city} onChange={(e) => setEditForm((p) => ({ ...p, city: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Pincode</Label><Input value={editForm.pincode} onChange={(e) => setEditForm((p) => ({ ...p, pincode: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
              </div>

              <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Address</Label><Input value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Blood Group</Label><Select value={editForm.blood_group || "none"} onValueChange={(value) => setEditForm((p) => ({ ...p, blood_group: value === "none" ? "" : value }))}><SelectTrigger className="h-12 rounded-2xl bg-slate-50/50 border-slate-200"><SelectValue placeholder="Select blood group" /></SelectTrigger><SelectContent><SelectItem value="none">Not specified</SelectItem>{BLOOD_GROUP_OPTIONS.map((bg) => (<SelectItem key={bg} value={bg}>{bg}</SelectItem>))}</SelectContent></Select></div>
                <div className="space-y-2"><Label className="text-[10px] font-black uppercase tracking-widest text-slate-600 ml-1">Emergency Contact</Label><Input value={editForm.emergency_contact} onChange={(e) => setEditForm((p) => ({ ...p, emergency_contact: e.target.value }))} className="h-12 rounded-2xl bg-slate-50/50 border-slate-200" /></div>
              </div>
            </div>
              </TabsContent>

              {canManagePermissions && (
                <TabsContent value="permissions" className="mt-4">
                  <PermissionsEditor
                    selectedPermissions={editSelectedPermissions}
                    onPermissionChange={setEditSelectedPermissions}
                    key={permissionsRefreshKey}
                    userId={editUserId}
                    orgType={orgType}
                    currentUserId={user?.user_id}
                  />
                </TabsContent>
              )}
            </Tabs>
          )}

          {editError && (
            <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{editError}</p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleUpdateUser} disabled={editSubmitting || editLoading}>
              {editSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Role Change Confirmation Dialog ──────────────────────────────── */}
      <ConfirmDialog
        open={roleChangeConfirmPending}
        onOpenChange={(o) => { if (!o) setRoleChangeConfirmPending(false); }}
        title="Discard permission overrides?"
        description="Changing this user's role will discard any custom permission overrides they have. Continue?"
        confirmLabel="Yes, change role"
        onConfirm={() => submitUserUpdate()}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete user?"
        description={`This will permanently delete ${deleteTarget?.first_name || deleteTarget?.username || "this user"}.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteSubmitting}
        onConfirm={handleDeleteUser}
      />

    </div>
  )
}

export default UserManagement